// ./src/utils/api/FormService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { FormField } from "./FormFieldService";

// GraphQL queries
const FORM_LIST_QUERY = gql`
  query FormList($filter: FormFilter, $params: PaginationAndSorting) {
    formList(filter: $filter, params: $params) {
      code
      data {
        id
        accountId
        path
        name
        description
        subject
        submitToEmails
        submitToUrl
        redirectUrl
        allowedReferers
        thankYouMessage
        formConfiguration
        publishedOn
        fieldsUpdatedOn
        submissionsQty
        submissionsQtyLimit
        dataRetentionDays
        accountEmailId
        connectorId
        filesConnectorId
        emailConnectorId
        workflowId
        notificationTemplateId
        confirmationTemplateId
        sendConfirmation
        allowFiles
        ignoreExtraFields
        doNotStoreSubmissionData
        gibberishCheck
        spamCheck
        ipFilter
        startsAt
        endsAt
        messageOnLimitReached
        messageBeforeStart
        messageAfterEnd
        messageOnDisabled
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_GET_QUERY = gql`
  query FormGet($id: ID) {
    formGet(id: $id) {
      code
      data {
        id
        accountId
        path
        name
        description
        subject
        submitToEmails
        submitToUrl
        redirectUrl
        allowedReferers
        thankYouMessage
        formConfiguration
        publishedOn
        fieldsUpdatedOn
        submissionsQty
        submissionsQtyLimit
        dataRetentionDays
        accountEmailId
        connectorId
        filesConnectorId
        emailConnectorId
        workflowId
        notificationTemplateId
        confirmationTemplateId
        sendConfirmation
        allowFiles
        ignoreExtraFields
        doNotStoreSubmissionData
        gibberishCheck
        spamCheck
        ipFilter
        startsAt
        endsAt
        messageOnLimitReached
        messageBeforeStart
        messageAfterEnd
        messageOnDisabled
        status
        rights
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        formFields {
          id
          formId
          orderNo
          name
          fieldType
          label
          required
          minValue
          maxValue
          options
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_GET_MANY_QUERY = gql`
  query FormGetMany($ids: [ID]) {
    formGetMany(ids: $ids) {
      code
      data {
        id
        accountId
        path
        name
        description
        subject
        submitToEmails
        submitToUrl
        redirectUrl
        allowedReferers
        thankYouMessage
        formConfiguration
        publishedOn
        fieldsUpdatedOn
        submissionsQty
        submissionsQtyLimit
        dataRetentionDays
        accountEmailId
        connectorId
        filesConnectorId
        emailConnectorId
        workflowId
        notificationTemplateId
        confirmationTemplateId
        sendConfirmation
        allowFiles
        ignoreExtraFields
        doNotStoreSubmissionData
        gibberishCheck
        spamCheck
        ipFilter
        startsAt
        endsAt
        messageOnLimitReached
        messageBeforeStart
        messageAfterEnd
        messageOnDisabled
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        formFields {
          id
          formId
          orderNo
          name
          fieldType
          label
          required
          minValue
          maxValue
          options
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const FORM_CREATE_MUTATION = gql`
  mutation FormCreate($params: FormInput) {
    formCreate(params: $params) {
      code
      data {
        id
        accountId
        path
        name
        description
        subject
        submitToEmails
        submitToUrl
        redirectUrl
        allowedReferers
        thankYouMessage
        formConfiguration
        publishedOn
        fieldsUpdatedOn
        submissionsQty
        submissionsQtyLimit
        dataRetentionDays
        accountEmailId
        connectorId
        filesConnectorId
        emailConnectorId
        workflowId
        notificationTemplateId
        confirmationTemplateId
        sendConfirmation
        allowFiles
        ignoreExtraFields
        doNotStoreSubmissionData
        gibberishCheck
        spamCheck
        ipFilter
        startsAt
        endsAt
        messageOnLimitReached
        messageBeforeStart
        messageAfterEnd
        messageOnDisabled
        status
        rights
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_UPDATE_MUTATION = gql`
  mutation FormUpdate($where: FormWhereInput, $params: FormInput) {
    formUpdate(where: $where, params: $params) {
      code
      data {
        id
        accountId
        path
        name
        description
        subject
        submitToEmails
        submitToUrl
        redirectUrl
        allowedReferers
        thankYouMessage
        formConfiguration
        publishedOn
        fieldsUpdatedOn
        submissionsQty
        submissionsQtyLimit
        dataRetentionDays
        accountEmailId
        connectorId
        filesConnectorId
        emailConnectorId
        workflowId
        notificationTemplateId
        confirmationTemplateId
        sendConfirmation
        allowFiles
        ignoreExtraFields
        doNotStoreSubmissionData
        gibberishCheck
        spamCheck
        ipFilter
        startsAt
        endsAt
        messageOnLimitReached
        messageBeforeStart
        messageAfterEnd
        messageOnDisabled
        status
        rights
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        formFields {
          id
          formId
          orderNo
          name
          fieldType
          label
          required
          minValue
          maxValue
          options
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_REMOVE_MUTATION = gql`
  mutation FormRemove($where: FormWhereInput) {
    formRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_REMOVE_MANY_MUTATION = gql`
  mutation FormRemoveMany($where: [FormWhereInput]) {
    formRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface Form {
  id?: string | undefined;
  accountId?: string | undefined;
  path?: string | undefined;
  name?: string | undefined;
  description?: string | undefined;
  subject?: string | undefined;
  submitToEmails?: string | undefined;
  submitToUrl?: string | undefined;
  redirectUrl?: string | undefined;
  allowedReferers?: string | undefined;
  thankYouMessage?: string | undefined;
  formConfiguration?: string | undefined;
  publishedOn?: string | undefined;
  fieldsUpdatedOn?: string | undefined;
  submissionsQtyLimit?: number | undefined;
  dataRetentionDays?: number | undefined;
  accountEmailId?: string | undefined;
  connectorId?: string | undefined;
  filesConnectorId?: string | undefined;
  emailConnectorId?: string | null | undefined;
  workflowId?: string | undefined;
  notificationTemplateId?: string | null | undefined;
  confirmationTemplateId?: string | null | undefined;
  sendConfirmation?: boolean | undefined;
  allowFiles?: boolean | undefined;
  ignoreExtraFields?: boolean | undefined;
  doNotStoreSubmissionData?: boolean | undefined;
  gibberishCheck?: string | undefined;
  spamCheck?: boolean | undefined;
  ipFilter?: string | null | undefined;
  startsAt?: string | undefined;
  endsAt?: string | undefined;
  messageOnLimitReached?: string | undefined;
  messageBeforeStart?: string | undefined;
  messageAfterEnd?: string | undefined;
  messageOnDisabled?: string | undefined;
  status?: number | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  removedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  userCreated?: User;
  userUpdated?: User;
  userRemoved?: User;
  submissionsQty?: number | null | undefined;
  formFields?: FormField[];
  duplicateOfId?: string | null | undefined;
  rights?: string[];
}

export interface FormInput {
  id?: string | null | undefined;
  accountId?: string | null | undefined;
  path?: string | null | undefined;
  name?: string | null | undefined;
  description?: string | null | undefined;
  subject?: string | null | undefined;
  submitToEmails?: string | null | undefined;
  submitToUrl?: string | null | undefined;
  redirectUrl?: string | null | undefined;
  allowedReferers?: string | null | undefined;
  thankYouMessage?: string | null | undefined;
  formConfiguration?: string | null | undefined;
  publishedOn?: string | null | undefined;
  fieldsUpdatedOn?: string | null | undefined;
  submissionsQtyLimit?: number | null | undefined;
  dataRetentionDays?: number | null | undefined;
  accountEmailId?: string | null | undefined;
  connectorId?: string | null | undefined;
  filesConnectorId?: string | null | undefined;
  emailConnectorId?: string | null | undefined;
  workflowId?: string | null | undefined;
  notificationTemplateId?: string | null | undefined;
  confirmationTemplateId?: string | null | undefined;
  sendConfirmation?: boolean | null | undefined;
  allowFiles?: boolean | null | undefined;
  ignoreExtraFields?: boolean | null | undefined;
  doNotStoreSubmissionData?: boolean | null | undefined;
  gibberishCheck?: string | undefined;
  spamCheck?: boolean | undefined;
  ipFilter?: string | null | undefined;
  startsAt?: string | null | undefined;
  endsAt?: string | null | undefined;
  messageOnLimitReached?: string | null | undefined;
  messageBeforeStart?: string | null | undefined;
  messageAfterEnd?: string | null | undefined;
  messageOnDisabled?: string | null | undefined;
  status?: number | null | undefined;
  duplicateOfId?: string | null | undefined;
}

export interface FormFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  path?: string[] | null | undefined;
  name?: string[] | null | undefined;
  notificationTemplateId?: string | null | undefined;
  confirmationTemplateId?: string | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface FormWhereInput {
  id?: string | undefined;
}

/**
 * Form service that handles all form-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class FormService {
  /**
   * Get a list of forms with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: FormFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(FORM_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single form by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(FORM_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple forms by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(FORM_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new form
   */
  static async create(params: FormInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(FORM_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing form
   */
  static async update(
    where: FormWhereInput,
    params: FormInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(FORM_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete a form
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(FORM_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple forms
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(FORM_REMOVE_MANY_MUTATION, {
      where,
    });
    return result;
  }
}
